import type { CatalogProduct, ProductVariant } from '../types/catalog';

/**
 * Extrae el talle limpio y el nombre base del producto.
 */
export function extractSizeAndBaseName(name: string): { baseName: string; size: string | null } {
  if (!name) return { baseName: '', size: null };

  // Patrones: TALLE 42, T42, T-42, T.42, Talle XL, T-M, etc.
  const sizeRegex = /\b(TALLE\s*([0-9]{1,3}|[SMLX]+)|T[-.\s]*([0-9]{2,3}|[SMLX]+))\b/i;
  const match = name.match(sizeRegex);

  if (match) {
    const rawSize = match[0];
    const cleanSize = match[2] || match[3] || rawSize;
    const baseName = name.replace(sizeRegex, '').replace(/\s+/g, ' ').trim();
    return {
      baseName,
      size: cleanSize.toUpperCase().replace(/^T/, '')
    };
  }

  // Números finales comunes en calzado e indumentaria (ej: "BOTIN DE SEGURIDAD 42")
  const endNumRegex = /\b(3[6-9]|4[0-9]|5[0-9]|6[0-4])\b$/;
  const endMatch = name.match(endNumRegex);
  const lower = name.toLowerCase();
  if (endMatch && (lower.includes('botin') || lower.includes('zapato') || lower.includes('camisa') || lower.includes('pantalon') || lower.includes('bermuda') || lower.includes('calzado') || lower.includes('zapatilla') || lower.includes('mameluco'))) {
    const baseName = name.replace(endNumRegex, '').replace(/\s+/g, ' ').trim();
    return {
      baseName,
      size: endMatch[0]
    };
  }

  return { baseName: name.trim(), size: null };
}

/**
 * Ordena los talles poniendo letras primero (XS, S, M, L, XL...) y luego números (34, 36, 38...).
 */
export function sortSizes(sizes: string[]): string[] {
  const sizeOrderAlpha = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL'];
  return [...new Set(sizes)].sort((a, b) => {
    const isNumA = /^\d+$/.test(a);
    const isNumB = /^\d+$/.test(b);

    // Si A es letra y B es número, la letra va antes
    if (!isNumA && isNumB) return -1;
    // Si A es número y B es letra, el número va después
    if (isNumA && !isNumB) return 1;

    // Si ambos son números
    if (isNumA && isNumB) {
      return parseInt(a, 10) - parseInt(b, 10);
    }

    // Si ambos son letras
    const idxA = sizeOrderAlpha.indexOf(a.toUpperCase());
    const idxB = sizeOrderAlpha.indexOf(b.toUpperCase());
    if (idxA !== -1 && idxB !== -1) {
      return idxA - idxB;
    }
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;

    return a.localeCompare(b);
  });
}

/**
 * Agrupa productos por familia (nombre base + marca) para no duplicar tarjetas en el catálogo.
 */
export function groupCatalogProducts(products: CatalogProduct[]): CatalogProduct[] {
  const groups = new Map<string, CatalogProduct>();

  for (const product of products) {
    const { baseName, size } = extractSizeAndBaseName(product.name);
    const groupKey = `${(product.brand || 'generico').toLowerCase()}:::${baseName.toLowerCase()}`;

    const variant: ProductVariant = {
      id: product.id,
      name: product.name,
      size: size || 'Único',
      sku: product.code || product.id,
      price: product.price,
      stock: product.stock
    };

    if (!groups.has(groupKey)) {
      // Usar el primer producto como plantilla maestro
      const masterProduct: CatalogProduct = {
        ...product,
        name: baseName || product.name,
        variants: [variant],
        availableSizes: size ? [size] : []
      };
      groups.set(groupKey, masterProduct);
    } else {
      const existing = groups.get(groupKey)!;
      existing.variants = existing.variants || [];
      existing.variants.push(variant);

      if (size && !existing.availableSizes?.includes(size)) {
        existing.availableSizes = sortSizes([...(existing.availableSizes || []), size]);
      }

      // Si el master no tenía imagen pero esta variante sí, usarla
      if ((!existing.image_url || existing.image_url.includes('placeholder')) && product.image_url && !product.image_url.includes('placeholder')) {
        existing.image_url = product.image_url;
        existing.imageUrl = product.imageUrl;
      }

      // Sumar stock
      existing.stock = (existing.stock || 0) + (product.stock || 0);
    }
  }

  return Array.from(groups.values());
}
